import { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import { IconButton, Tooltip } from '@folio/stripes/components';

import css from './VoiceInput.css';

const propTypes = {
  onVoiceInput: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  language: PropTypes.string,
};

const defaultProps = {
  disabled: false,
  language: 'ar-SA', // Default to Arabic Saudi
};

const VoiceInput = ({ onVoiceInput, disabled, language }) => {
  const intl = useIntl();
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    // Check if Web Speech API is supported
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      setIsSupported(true);
      
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = language;
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        setIsListening(true);
        setError(null);
      };

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        onVoiceInput(transcript);
        setIsListening(false);
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setError(event.error);
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    } else {
      setIsSupported(false);
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [language, onVoiceInput]);

  const startListening = () => {
    if (!isSupported || !recognitionRef.current || disabled) return;

    try {
      recognitionRef.current.start();
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
      setError('start_error');
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
    }
  };

  const handleClick = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const getTooltipText = () => {
    if (!isSupported) {
      return intl.formatMessage({ id: 'ui-plugin-smart-help.voice.notSupported' });
    }
    
    if (error) {
      return intl.formatMessage({ id: 'ui-plugin-smart-help.voice.error' });
    }
    
    if (isListening) {
      return intl.formatMessage({ id: 'ui-plugin-smart-help.voice.listening' });
    }
    
    return intl.formatMessage({ id: 'ui-plugin-smart-help.voice.tooltip' });
  };

  const getIconName = () => {
    if (error) return 'exclamation-circle';
    if (isListening) return 'microphone-slash';
    return 'microphone';
  };

  const getButtonClass = () => {
    let classes = css.voiceButton;
    if (isListening) classes += ` ${css.listening}`;
    if (error) classes += ` ${css.error}`;
    if (!isSupported) classes += ` ${css.notSupported}`;
    return classes;
  };

  if (!isSupported) {
    return null; // Don't render if not supported
  }

  return (
    <Tooltip 
      id={`voice-tooltip-${Date.now()}`}
      text={getTooltipText()}
      placement="top"
    >
      {({ ref, ariaIds }) => (
        <IconButton
          ref={ref}
          icon={getIconName()}
          onClick={handleClick}
          disabled={disabled || !isSupported}
          className={getButtonClass()}
          {...ariaIds}
        />
      )}
    </Tooltip>
  );
};

VoiceInput.propTypes = propTypes;
VoiceInput.defaultProps = defaultProps;

export default VoiceInput;